/**
 * AOCTANT announcement marquee custom element.
 *
 * Duplicates the semantic message group enough times to cover the viewport,
 * then duplicates the whole rail for a seamless CSS-transform loop.
 * Pauses on fine-pointer hover and when the section is fully offscreen.
 */

const SPEED_TO_PX_PER_SECOND = {
  ultra_slow: 25,
  slow: 45,
  normal: 70,
  fast: 110,
};

class AoctantAnnouncementMarquee extends HTMLElement {
  #resizeHandler = null;
  #observer = null;

  connectedCallback() {
    this.refs = {
      track: this.querySelector('[ref="track"]'),
      rail: this.querySelector('[ref="rail"]'),
      group: this.querySelector('[ref="group"]'),
    };

    if (!this.refs.track || !this.refs.rail || !this.refs.group) return;

    this.#resizeHandler = this.#debounce(() => this.#build(), 250);

    this.#build();
    window.addEventListener('resize', this.#resizeHandler);
    this.#setupObserver();
  }

  disconnectedCallback() {
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
    }
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  #build() {
    const { track, rail, group } = this.refs;
    const viewportWidth = this.offsetWidth || window.innerWidth;
    const groupWidth = group.getBoundingClientRect().width;

    // Remove any previously generated clones.
    rail.querySelectorAll('.aoctant-announcement-marquee__group--clone').forEach((clone) => clone.remove());
    track.querySelectorAll('.aoctant-announcement-marquee__rail--clone').forEach((clone) => clone.remove());

    // Number of semantic groups needed to cover the viewport.
    const groupCount = groupWidth > 0 ? Math.max(2, Math.ceil(viewportWidth / groupWidth)) : 2;

    // Add visual-only group clones inside the rail.
    for (let i = 1; i < groupCount; i++) {
      const clone = group.cloneNode(true);
      clone.classList.add('aoctant-announcement-marquee__group--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('ref');
      rail.appendChild(clone);
    }

    // Duplicate the entire rail so the CSS animation can reset seamlessly.
    const railClone = rail.cloneNode(true);
    railClone.classList.add('aoctant-announcement-marquee__rail--clone');
    railClone.setAttribute('aria-hidden', 'true');
    railClone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
    track.appendChild(railClone);

    // Apply duration and direction.
    const speed = this.dataset.speed || 'normal';
    const pxPerSecond = SPEED_TO_PX_PER_SECOND[speed] || SPEED_TO_PX_PER_SECOND.normal;
    const distance = rail.getBoundingClientRect().width || viewportWidth;
    this.style.setProperty('--announcement-duration', (distance / pxPerSecond).toFixed(3) + 's');

    const direction = this.dataset.direction || 'left';
    this.style.setProperty('--announcement-direction', direction === 'right' ? 'reverse' : 'normal');

    // Start the animation on the next frame so the cloned layout is settled.
    requestAnimationFrame(() => {
      track.classList.add('aoctant-announcement-marquee__track--animated');
    });
  }

  #setupObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.classList.toggle('aoctant-announcement-marquee--offscreen', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    this.#observer.observe(this);
  }

  #debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('aoctant-announcement-marquee')) {
  customElements.define('aoctant-announcement-marquee', AoctantAnnouncementMarquee);
}

